import { type NumericInputHookConfig, useNumericInput } from '@/hooks/numeric'
import {
  type FocusEvent,
  type InputHTMLAttributes,
  type KeyboardEvent,
  useCallback,
  useRef,
} from 'react'
import { type ControlStatus } from '@/components/inputs/control-status'
import { NEVER, type Observable } from 'rxjs'
import { useFocusTracking, useObservableFocus } from '@/hooks/focus-hooks'
import cx from 'classnames'
import { IconButton } from '@/components/IconButton'

import { asNumber } from '@/utils/number-util'
import { isNil } from 'lodash-es'
import styles from './NumericSpinnerInput.module.css'

export interface NumericSpinnerInputProps
  extends
    Omit<
      InputHTMLAttributes<HTMLInputElement>,
      'value' | 'onChange' | 'disabled'
    >,
    NumericInputHookConfig {
  isDisabled?: boolean
  status?: ControlStatus
  focus$?: Observable<unknown>
  invalid?: boolean
  testId?: string
  /**
   * Replaces the "+" button's step, for when stepping follows different rules
   * than typing.
   */
  onIncrement?: () => unknown
  /**
   * Replaces the "−" button's step, for when stepping follows different rules
   * than typing.
   */
  onDecrement?: () => unknown
}

/**
 * A numeric input with buttons that step the value up and down.
 */
export function NumericSpinnerInput({
  className,
  value,
  onChange,
  precision = 0,
  locale,
  onBlur,
  focus$ = NEVER,
  status = 'default',
  mode = 'live',
  allowNegative,
  invalid,
  testId,
  onIncrement: onIncrementOverride,
  onDecrement: onDecrementOverride,
  isDisabled,
  ...props
}: NumericSpinnerInputProps) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  useObservableFocus(focus$, inputRef)
  const connector = useNumericInput({
    value,
    precision,
    mode,
    locale,
    onChange,
    allowNegative,
  })
  const { commit } = connector
  const blur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      if (mode === 'draft') {
        commit()
      }
      if (onBlur) {
        onBlur(e)
      }
    },
    [onBlur, mode, commit],
  )
  const [focusProps, isFocused] = useFocusTracking({
    onBlur: blur,
    onFocus: props.onFocus,
  })

  const keyDown = useCallback(
    (e: KeyboardEvent) => {
      connector.keyDownPreventNonNumeric(e)
      if (e.key === 'Enter' && mode === 'draft') {
        connector.commit()
      }
    },
    [connector, mode],
  )

  const onIncrement = useCallback(() => {
    if (onIncrementOverride) {
      return onIncrementOverride()
    }
    const max = !isNil(props.max) ? asNumber(props.max) : undefined
    const currentValue = asNumber(connector.value)
    const step = props.step ? asNumber(props.step) : 1
    const newValue = currentValue + step
    if (isNil(max)) {
      return connector.onChange(newValue.toString())
    }

    if (newValue <= max) {
      return connector.onChange(newValue.toString())
    }
  }, [connector, onIncrementOverride, props.max, props.step])

  const onDecrement = useCallback(() => {
    if (onDecrementOverride) {
      return onDecrementOverride()
    }
    const min = !isNil(props.min) ? asNumber(props.min) : undefined
    const currentValue = asNumber(connector.value)
    const step = props.step ? asNumber(props.step) : 1
    const newValue = currentValue - step

    if (isNil(min)) {
      return connector.onChange(newValue.toString())
    }

    if (newValue >= min) {
      return connector.onChange(newValue.toString())
    }
  }, [connector, onDecrementOverride, props.min, props.step])

  return (
    <div
      className={cx(
        styles.spinnerContainer,
        isFocused && styles.focused,
        isDisabled && styles.disabled,
        invalid && styles.isNotValid,
        styles[status],
        className,
      )}
    >
      <IconButton
        name="subtract"
        label="Decrease"
        onPress={onDecrement}
        isDisabled={isDisabled}
        size={16}
      />

      <input
        type="number"
        min={props.min}
        max={props.max}
        disabled={isDisabled}
        className={cx(styles.input, styles.spinner)}
        value={connector.value}
        onChange={(e) => connector.onChange(e.target.value)}
        data-test={testId}
        {...focusProps}
        onKeyDown={keyDown}
      />

      <IconButton
        name="add"
        label="Increase"
        onPress={onIncrement}
        isDisabled={isDisabled}
        size={16}
      />
    </div>
  )
}
